import { signalStore, withState, withMethods, withHooks, patchState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import { LoggerService } from '../core/services/logger.service';

/**
 * WebSocket Configuration Defaults
 * These values are tuned for optimal balance of responsiveness and resource usage
 */

/** Time between reconnection attempts (5 seconds) */
export const WS_RECONNECT_INTERVAL_MS = 5000;

/** Maximum reconnection attempts before giving up (10 attempts = ~50s total) */
export const WS_MAX_RECONNECT_ATTEMPTS = 10;

/** Heartbeat/ping interval to keep connection alive (30 seconds) */
export const WS_HEARTBEAT_INTERVAL_MS = 30000;

/** Minimum time between refresh calls after reconnection (5 seconds) */
export const WS_MIN_REFRESH_INTERVAL_MS = 5000;

/** Maximum polling interval during adaptive backoff (30 seconds) */
export const WS_MAX_POLLING_INTERVAL_MS = 30000;

/** Base polling interval (1 second) */
export const WS_BASE_POLLING_INTERVAL_MS = 1000;

/** Maximum jitter for polling interval (500ms) */
export const WS_POLLING_JITTER_MS = 500;

/**
 * Typed API endpoints for the application
 */
export interface ApiEndpoints {
  // Auth endpoints
  auth: {
    login: string;
    logout: string;
    refresh: string;
    register: string;
    verify: string;
    validate?: string;
  };
  // User management
  users: {
    list: string;
    get: (id: string) => string;
    create: string;
    update: (id: string) => string;
    delete: (id: string) => string;
    profile: string;
  };
  // Dashboard
  dashboard: {
    root: string;
    overview: string;
    metrics: string;
    performance: string;
  };
  // System health
  systemHealth: {
    status: string;
    metrics: string;
    history: string;
  };
  // Symbol mapping
  symbolMapping: {
    list: string;
    search: string;
    create: string;
    update: (id: string) => string;
    delete: (id: string) => string;
    analytics: string;
  };
  // Order management
  orders: {
    list: string;
    get: (id: string) => string;
    create: string;
    cancel: (id: string) => string;
    history: string;
  };
  // Risk management
  risk: {
    positions: string;
    limits: string;
    alerts: string;
    metrics: string;
  };
  // Environment config
  config: {
    list: string;
    get: (key: string) => string;
    update: string;
    apply: string;
  };
  // Schema management
  schema: {
    list: string;
    migrations: string;
    dictionary: string;
    apply: string;
  };
  // Reports
  reports: {
    list: string;
    generate: string;
    download: (id: string) => string;
  };
  // Alert management
  alerts: {
    definitions: {
      list: string;
      get: (id: string) => string;
      create: string;
      update: (id: string) => string;
      delete: (id: string) => string;
      trigger: (id: string) => string;
    };
    instances: {
      list: string;
      get: (id: string) => string;
      acknowledge: (id: string) => string;
      resolve: (id: string) => string;
    };
    statistics: string;
  };
}

/**
 * WebSocket endpoints for real-time data
 */
export interface WsEndpoints {
  base: string;
  marketData: string;
  trading: string;
  admin: string;
  risk: string;
  orderBook: string;
  symbolMapping: string;
  systemHealth: string;
  alerts: string;
}

/**
 * WebSocket connection configuration
 */
export interface WsConnectionConfig {
  /** Reconnect interval in milliseconds (default: 5000) */
  reconnectInterval: number;
  /** Maximum reconnect attempts (default: 10) */
  maxReconnectAttempts: number;
  /** Heartbeat interval in milliseconds (default: 30000) */
  heartbeatInterval: number;
  /** Minimum refresh interval after reconnection in milliseconds (default: 5000) */
  minRefreshInterval: number;
  /** Enable adaptive polling backoff (default: true) */
  adaptivePolling: boolean;
  /** Maximum polling interval in milliseconds for adaptive backoff (default: 30000) */
  maxPollingInterval: number;
}

/**
 * Runtime environment configuration passed to ConfigStore.updateRuntimeConfig()
 */
export interface RuntimeEnvironmentConfig {
  production: boolean;
  api?: {
    baseUrl?: string;
  };
  apiUrl?: string;
  websocket?: {
    baseUrl?: string;
  };
  features?: Partial<FeatureFlags>;
  buildTime?: string;
}

/**
 * Remote configuration interface
 */
export interface RemoteConfig {
  version: string;
  apiUrl: string;
  websocketUrl: string;
  features: FeatureFlags;
  lastUpdated: Date;
  apiVersion?: string;
  endpoints?: Record<string, string>;
}

/**
 * Feature flags for the application
 */
export interface FeatureFlags {
  enableMockData: boolean;
  enableWebSockets: boolean;
  enableDebugMode: boolean;
  enableRealTimeUpdates: boolean;
  enableRealTimeData?: boolean; // Alias for enableRealTimeUpdates
  enableAdvancedAnalytics: boolean;
  enableBulkOperations: boolean;
  enableSystemHealthMonitoring: boolean;
  enablePredictiveAnalytics: boolean;
  enableAuditLogging: boolean;
  experimentalFeatures: boolean;
}

/**
 * Role definition interface
 */
export interface RoleDefinition {
  id: string;
  name: string;
  permissions: string[];
  description: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  inherits?: string[];
}

export interface ConfigState {
  environment: string;
  apiUrl: string;
  apiVersion: string;
  websocketUrl: string;
  marketDataWsUrl: string;
  features: {
    enableWebSockets: boolean;
    enableMockData: boolean;
    enableAdvancedAnalytics?: boolean;
  };
  ready: boolean;
  wsConnectionConfig: WsConnectionConfig;
}

export const ConfigStore = signalStore(
  { providedIn: 'root' },
  withState<ConfigState>({
    environment: environment.production ? 'production' : 'development',
    apiUrl: environment.apiUrl,
    apiVersion: environment.apiVersion || 'v1',
    websocketUrl: environment.websocketUrl,
    marketDataWsUrl: environment.marketDataWsUrl,
    features: {
      enableWebSockets: environment.features.enableWebSockets,
      enableMockData: environment.features.enableMockData,
      enableAdvancedAnalytics: environment.features.enableAdvancedAnalytics
    },
    ready: true,
    wsConnectionConfig: {
      reconnectInterval: environment.wsReconnectInterval ?? WS_RECONNECT_INTERVAL_MS,
      maxReconnectAttempts: environment.wsMaxReconnectAttempts ?? WS_MAX_RECONNECT_ATTEMPTS,
      heartbeatInterval: environment.wsHeartbeatInterval ?? WS_HEARTBEAT_INTERVAL_MS,
      minRefreshInterval: WS_MIN_REFRESH_INTERVAL_MS,
      adaptivePolling: true,
      maxPollingInterval: WS_MAX_POLLING_INTERVAL_MS
    }
  }),
  withMethods((store) => {
    const logger = inject(LoggerService);

    const buildUrl = (path: string) => {
      const base = store.apiUrl();
      // API URL already includes /api/v1 from environment
      return `${base}/${path}`;
    };

    const buildWsUrl = (path: string) => {
      const base = store.websocketUrl();
      return base.endsWith('/') ? `${base}${path}` : `${base}/${path}`;
    };

    /** Validate that a URL uses an allowed scheme (http/https for API, ws/wss for WebSocket) */
    const isValidApiUrl = (url: string): boolean => {
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
      } catch {
        return false;
      }
    };

    const isValidWsUrl = (url: string): boolean => {
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'ws:' || parsed.protocol === 'wss:';
      } catch {
        return false;
      }
    };

    /** Sanitize a URL string for safe logging (truncate + strip control chars) */
    const sanitizeForLog = (url: string): string =>
      url.slice(0, 200).replace(/[\x00-\x1f<>&"']/g, '');

    return {
      
      /**
       * Get typed API endpoints
       */
      api: (): ApiEndpoints => ({
        auth: {
          login: buildUrl('auth/login'),
          logout: buildUrl('auth/logout'),
          refresh: buildUrl('auth/refresh'),
          register: buildUrl('auth/register'),
          verify: buildUrl('auth/verify')
        },
        users: {
          list: buildUrl('users'),
          get: (id: string) => buildUrl(`users/${id}`),
          create: buildUrl('users'),
          update: (id: string) => buildUrl(`users/${id}`),
          delete: (id: string) => buildUrl(`users/${id}`),
          profile: buildUrl('users/profile')
        },
        dashboard: {
          root: buildUrl('dashboard'),
          overview: buildUrl('dashboard/overview'),
          metrics: buildUrl('dashboard/metrics'),
          performance: buildUrl('dashboard/performance')
        },
        systemHealth: {
          status: buildUrl('system/health'),
          metrics: buildUrl('system/metrics'),
          history: buildUrl('system/history')
        },
        symbolMapping: {
          list: buildUrl('admin/symbol-mappings'),
          search: buildUrl('admin/symbol-mappings/search'),
          create: buildUrl('admin/symbol-mappings'),
          update: (id: string) => buildUrl(`admin/symbol-mappings/${id}`),
          delete: (id: string) => buildUrl(`admin/symbol-mappings/${id}`),
          analytics: buildUrl('admin/symbol-mappings/analytics')
        },
        orders: {
          list: buildUrl('orders'),
          get: (id: string) => buildUrl(`orders/${id}`),
          create: buildUrl('orders'),
          cancel: (id: string) => buildUrl(`orders/${id}/cancel`),
          history: buildUrl('orders/history')
        },
        risk: {
          positions: buildUrl('risk/positions'),
          limits: buildUrl('risk/limits'),
          alerts: buildUrl('risk/alerts'),
          metrics: buildUrl('risk/metrics')
        },
        config: {
          list: buildUrl('config'),
          get: (key: string) => buildUrl(`config/${key}`),
          update: buildUrl('config'),
          apply: buildUrl('config/apply')
        },
        schema: {
          list: buildUrl('schema'),
          migrations: buildUrl('schema/migrations'),
          dictionary: buildUrl('schema/dictionary'),
          apply: buildUrl('schema/apply')
        },
        reports: {
          list: buildUrl('reports'),
          generate: buildUrl('reports/generate'),
          download: (id: string) => buildUrl(`reports/${id}/download`)
        },
        alerts: {
          definitions: {
            list: buildUrl('alerts/definitions'),
            get: (id: string) => buildUrl(`alerts/definitions/${id}`),
            create: buildUrl('alerts/definitions'),
            update: (id: string) => buildUrl(`alerts/definitions/${id}`),
            delete: (id: string) => buildUrl(`alerts/definitions/${id}`),
            trigger: (id: string) => buildUrl(`alerts/definitions/${id}/trigger`)
          },
          instances: {
            list: buildUrl('alerts/instances'),
            get: (id: string) => buildUrl(`alerts/instances/${id}`),
            acknowledge: (id: string) => buildUrl(`alerts/instances/${id}/acknowledge`),
            resolve: (id: string) => buildUrl(`alerts/instances/${id}/resolve`)
          },
          statistics: buildUrl('alerts/statistics')
        }
      }),

      /**
       * Get typed WebSocket endpoints
       */
      ws: (): WsEndpoints => ({
        base: store.websocketUrl(),
        marketData: environment.marketDataWsUrl || buildWsUrl('market-data'),
        trading: environment.tradingWsUrl || buildWsUrl('trading'),
        admin: environment.adminWsUrl || buildWsUrl('admin'),
        risk: environment.riskManagementWsUrl || buildWsUrl('risk'),
        orderBook: environment.orderBookWsUrl || buildWsUrl('order-book'),
        symbolMapping: environment.symbolMappingWsUrl || buildWsUrl('symbols'),
        systemHealth: environment.systemHealthWsUrl || buildWsUrl('system-health'),
        alerts: buildWsUrl('alerts')
      }),

      /**
       * Get base API URL
       */
      getApiUrl: () => store.apiUrl(),

      /**
       * Build full API endpoint URL (for backward compatibility)
       */
      buildApiUrl: (endpoint: string) => {
        const base = store.apiUrl();
        // Handle absolute URLs
        if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
          return endpoint;
        }
        // Handle WebSocket endpoints specially
        if (endpoint === 'websocket' || endpoint === 'ws') {
          return store.websocketUrl();
        }
        if (endpoint === 'market-data-ws') {
          return store.marketDataWsUrl();
        }
        // Remove leading slash if present
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        return `${base}/${cleanEndpoint}`;
      },
      
      endpoints: () => ({
        api: store.apiUrl(),
        websocket: store.websocketUrl(),
        marketData: store.marketDataWsUrl()
      }),

      // Note: `features` is already a state signal, accessed via store.features()
      // Do not redefine it here to avoid NgRx SignalStore member override warnings

      isProduction: () => store.environment() === 'production',
      isDevelopment: () => store.environment() === 'development',
      isMockEnabled: () => store.features().enableMockData,
      
      updateConfig: (config: Partial<ConfigState>) => {
        patchState(store, config);
      },

      /**
       * Update configuration with runtime environment settings
       */
      updateRuntimeConfig: (runtimeEnv: RuntimeEnvironmentConfig) => {
        if (!environment.production) {
          logger.debug('[ConfigStore] Updating config with runtime environment:', { runtimeEnv });
        }

        // Validate URL overrides before applying — reject non-http(s)/ws(s) schemes
        let nextApiUrl = store.apiUrl();
        const candidateApiUrl = runtimeEnv.api?.baseUrl ?? runtimeEnv.apiUrl;
        if (candidateApiUrl) {
          if (isValidApiUrl(candidateApiUrl)) {
            nextApiUrl = candidateApiUrl;
          } else {
            logger.warn('[ConfigStore] Rejected invalid API URL from runtime config', { url: sanitizeForLog(candidateApiUrl) });
          }
        }

        let nextWsUrl = store.websocketUrl();
        const candidateWsUrl = runtimeEnv.websocket?.baseUrl;
        if (candidateWsUrl) {
          if (isValidWsUrl(candidateWsUrl)) {
            nextWsUrl = candidateWsUrl;
          } else {
            logger.warn('[ConfigStore] Rejected invalid WebSocket URL from runtime config', { url: sanitizeForLog(candidateWsUrl) });
          }
        }

        // Only pick feature keys declared in ConfigState.features to avoid leaking
        // extra FeatureFlags fields (e.g. enableBulkOperations) into the store state
        const currentFeatures = store.features();
        const incomingFeatures = runtimeEnv.features ?? {};
        patchState(store, {
          environment: runtimeEnv.production ? 'production' : 'development',
          apiUrl: nextApiUrl,
          websocketUrl: nextWsUrl,
          features: {
            enableWebSockets: incomingFeatures.enableWebSockets ?? currentFeatures.enableWebSockets,
            enableMockData: incomingFeatures.enableMockData ?? currentFeatures.enableMockData,
            enableAdvancedAnalytics: incomingFeatures.enableAdvancedAnalytics ?? currentFeatures.enableAdvancedAnalytics
          },
          ready: true
        });
      },

      /**
       * Get current configuration
       */
      currentConfig: () => ({
        environment: store.environment(),
        apiUrl: store.apiUrl(),
        apiVersion: store.apiVersion(),
        websocketUrl: store.websocketUrl(),
        marketDataWsUrl: store.marketDataWsUrl(),
        features: store.features(),
        ready: store.ready()
      }),

      /**
       * Validate configuration settings
       */
      validateConfiguration: () => {
        const config = store;
        const errors: string[] = [];
        const warnings: string[] = [];

        // Validate API URL
        if (!config.apiUrl()) {
          errors.push('API URL is required');
        } else if (!config.apiUrl().startsWith('http')) {
          errors.push('API URL must be a valid HTTP/HTTPS URL');
        }

        // Validate WebSocket URL
        if (config.features().enableWebSockets && !config.websocketUrl()) {
          warnings.push('WebSocket URL is missing but WebSockets are enabled');
        }

        // Validate environment
        if (!['development', 'production', 'staging'].includes(config.environment())) {
          warnings.push(`Unknown environment: ${config.environment()}`);
        }

        return {
          valid: errors.length === 0,
          errors,
          warnings
        };
      },

      /**
       * Check if configuration is ready
       */
      isReady: () => store.ready(),

      /**
       * Get WebSocket connection configuration
       * Note: `wsConnectionConfig` is already a state signal, accessed via store.wsConnectionConfig()
       * Removed duplicate method to avoid NgRx SignalStore member override warnings
       */
      getWsConfig: (): WsConnectionConfig => store.wsConnectionConfig()
    };
  }),
  withHooks((store) => {
    const hookLogger = inject(LoggerService);
    return {
      onInit() {
        if (!environment.production) {
          hookLogger.debug('[ConfigStore] Initialized', {
            environment: store.environment(),
            apiUrl: store.apiUrl(),
            features: store.features()
          });
        }
      }
    };
  })
);
